<template>
  <layout name="BaseLayout">
    <div>
      <v-data-table
        ref="dataTable"
        dense
        :headers="tableHeaders"
        :items="items"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="elevation-1"
        :loading="loading"
        :loading-text="loadingText"
        :footer-props="{
          itemsPerPageOptions: rowsPerPage,
          itemsPerPageAllText: allTextForPerPage,
          itemsPerPageText: itemsPerPageText,
        }"
        :options="options"
        :no-results-text="filtersNoResultText"
        :multi-sort="true"
      >
        <!-- formatear el texto de la paginacion -->
        <template v-slot:footer.page-text="props">
          {{ props.pageStart }} - {{ props.pageStop }} de
          {{ props.itemsLength }}
        </template>

        <!-- formatear columna de fecha  -->
        <template v-slot:item.fecha="{ item }">
          {{ item.fecha | formatDateDDMMYYYY }}
        </template>

        <!-- formatear columna de importe -->
        <template v-slot:item.importe="{ item }">
          {{ item.importe | formatFloat }}
        </template>

        <!-- cabecera (toolbar + filtros) antes del header de la tabla -->
        <template v-slot:top>
          <v-toolbar text color="white">
            <v-toolbar-title>Remesas</v-toolbar-title>

            <v-divider class="mx-4" inset vertical></v-divider>

            <v-btn
              :loading="loading"
              :disabled="loading"
              @click="refreshItems"
              text
              color="primary"
            >
              Actualizar
              <v-icon right dark>mdi-refresh</v-icon>
            </v-btn>

            <template v-if="userCanEdit && isShowCrearRemesas()">
              <v-spacer></v-spacer>

              <v-btn @click="addRemesa()" text dark color="primary">
                <v-icon right dark>mdi-plus</v-icon>
                <span class="ml-1">Añadir</span>
              </v-btn>
            </template>
          </v-toolbar>
        </template>

        <!-- columna de acciones -->
        <template v-if="userCanEdit" v-slot:item.actions="{ item }">
          <v-btn
            small
            color="primary"
            icon
            @click="descargar(item)"
            :loading="loadingDescarga && isCurrentDescargaItemLoading(item)"
          >
            <v-icon small>
              mdi-file-download-outline
            </v-icon>
          </v-btn>
        </template>

        <!-- Se muestra con resultado vacio -->
        <template v-slot:no-data>
          {{ filtersNoResultText }}
        </template>
      </v-data-table>

      <!-- popup de confirmación de borrado -->
      <confirm ref="remesaConfirm"></confirm>
    </div>
  </layout>
</template>

<script>
import Layout from "@/components/layouts/Layout";
import { mapGetters, mapMutations } from "vuex";
import { getAll, newRemesa, descargarFichero } from "@/api/remesas.api";
import Confirm from "@/components/Confirm";
import DateUtils from "@/libraries/DateUtils";

export default {
  name: "Remesas",
  components: {
    Layout,
    Confirm,
  },

  data: () => ({
    items: [],
    headers: [],
    sortBy: "fecha",
    sortDesc: true,
    rowsPerPage: [25, 50, 75, 100, -1],
    options: {
      itemsPerPage: 25,
    },

    loading: false,
    loadingDescarga: false,
    loadingDescargaActionRef: "",

    // textos varios
    filtersNoResultText: "No se han encontrado resultados",
    loadingText: "Cargando ...",
    itemsPerPageText: "Filas por página:",
    allTextForPerPage: "Todas",

    // Administracion de roles de usuario
    authorizedRoles: ["ROLE_SUPER_ADMIN"],
  }),

  beforeMount() {
    if (
      this.$store.getters["Auth/authRoles"] !== undefined &&
      !this.authorizedRoles.includes(this.$store.getters["Auth/authRoles"][0])
    ) {
      this.loading = false;
      this.$router.push("/prematriculas");
    }
  },

  created() {
    this.initialize();
  },

  watch: {
    cursoActual() {
      this.items = [];
      this.initialize();
    },
  },

  computed: {
    ...mapGetters("App", {
      cursoActual: "GET_CURSO_ACTUAL",
    }),

    ...mapGetters("Auth", {
      userCanEdit: "userCanEdit",
    }),

    tableHeaders() {
      return this.headers;
    },
  },

  methods: {
    ...mapMutations("Snack", {
      setSnack: "SET_SNACK_OPTIONS",
    }),

    isShowCrearRemesas() {
      if (this.cursoActual == null) {
        return false;
      }

      let fechaActual = new Date();
      let anioActual = fechaActual.getFullYear();
      let anioCursoActual = parseInt(this.cursoActual.anio);

      return this.userCanEdit && anioCursoActual == anioActual;
    },

    async initialize() {
      this.loading = true;

      if (this.cursoActual) {
        this.headers = [
          { text: "Id Remesa", align: "start", value: "id" },
          {
            text: "Fecha creación",
            align: "start",
            value: "fecha",
          },
          {
            text: "Nº de recibos",
            align: "end",
            value: "nroRecibos",
          },
          {
            text: "Importe total",
            align: "end",
            value: "importe",
          },
        ];

        if (this.userCanEdit) {
          this.headers.push({ text: "", value: "actions", sortable: false });
        }

        await getAll(this.cursoActual.id).then(
          (response) => (this.items = response.data)
        );
      }

      this.loading = false;
    },

    async refreshItems() {
      this.loading = true;
      this.items = [];
      await getAll(this.cursoActual.id).then(
        (response) => (this.items = response.data)
      );
      this.loading = false;
    },

    async addRemesa() {
      let title = "Nueva remesa";
      let subtitle = "¿Estás seguro?";
      let message = "Se va a generar una nueva remesa para el curso actual";
      let message2 = null;

      // confirmacion
      if (
        await this.$refs.remesaConfirm.open(
          title,
          subtitle,
          message,
          message2,
          {
            color: "primary",
            width: 300,
          }
        )
      ) {
        const remesa = {
          curso: this.cursoActual.id,
        };

        await newRemesa(remesa)
          .then((response) => {
            this.items.push(response.data);
            this.setSnack({
              active: true,
              timeout: 1500,
              color: this.$getStringFor("snack", "successColor"),
              text: this.$getStringFor("snack", "successText"),
            });
          })
          .catch((error) => {
            let message = this.$getStringFor("snack", "errorText");
            let timeout = 1500;

            if (error.response.status == 409) {
              message = error.response.data.detail;
              timeout = 3000;
            }

            this.setSnack({
              active: true,
              timeout,
              color: this.$getStringFor("snack", "errorColor"),
              text: message,
            });
          });
      }
    },

    async descargar(item) {
      this.loadingDescarga = true;
      this.loadingDescargaActionRef = `btn-matricula-${item.id}`;

      await descargarFichero(item.id)
        .then((response) => {
          this.loadingDescarga = false;
          this.loadingDescargaActionRef = "";

          let dateUtils = new DateUtils(item.fecha);
          let filename = dateUtils.formatDateDDMMYYYYToFilename();

          // Insert a link that allows the user to download the PDF file
          var link = document.createElement("a");
          link.href =
            "data:text/plain;charset=utf-8," +
            encodeURIComponent(response.data.output);
          link.download = `${filename}.q19`;
          link.click();
        })
        .catch((error) => {
          console.error(error);
          this.loadingDescarga = false;
          this.loadingDescargaActionRef = "";
        });
    },

    /**
     * Determina en que item del listado se ha clicado y hay que cargar el loading
     */
    isCurrentDescargaItemLoading(item) {
      return this.loadingDescargaActionRef == `btn-matricula-${item.id}`;
    },
  },
};
</script>
